/**
 * React Query hooks for the framework-assessments HTTP surface (P10).
 *
 * Mirrors backend/framework/assessment_router.py +
 * backend/framework/evidence_analyser_router.py.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import { frameworkAssessmentKeys } from "shared/api/queryClient";
import type {
  AssessmentQueueRow,
  ControlLocation,
  AnalyseBatchIn,
  AnalyseControlIn,
  AnalyserRun,
  AssessmentAction,
  AssessmentTransitionIn,
  AssessmentUpsertIn,
  CycleCreateIn,
  CycleProgress,
  EvidenceArtefact,
  EvidenceArtefactIn,
  FrameworkAssessment,
  FrameworkCatalogue,
  FrameworkCycle,
  FrameworkSummary,
} from "shared/api/types";

// ── Catalogue reads ────────────────────────────────────────────────

export function useFrameworks(): UseQueryResult<FrameworkSummary[], ApiError> {
  return useQuery({
    queryKey: frameworkAssessmentKeys.frameworks(),
    queryFn: () => api.get<FrameworkSummary[]>("/v1/framework/frameworks"),
    staleTime: 5 * 60_000,
  });
}

// P222: open-cycle assessment coverage for the Home card.
export function useCycleProgress(): UseQueryResult<CycleProgress[], ApiError> {
  return useQuery({
    queryKey: [...frameworkAssessmentKeys.frameworks(), "cycle-progress"],
    queryFn: () => api.get<CycleProgress[]>("/v1/framework/cycles/progress"),
    retry: false, // 403 for non-framework users -> the card renders null
    staleTime: 60_000,
  });
}

export function useCatalogue(
  code: string | undefined,
): UseQueryResult<FrameworkCatalogue, ApiError> {
  return useQuery({
    queryKey: code
      ? frameworkAssessmentKeys.catalogue(code)
      : ["framework-assessments", "catalogue", "_disabled"],
    queryFn: () =>
      api.get<FrameworkCatalogue>(
        `/v1/framework/frameworks/${encodeURIComponent(code ?? "")}/catalogue`,
      ),
    enabled: !!code,
    staleTime: 5 * 60_000,
  });
}

/**
 * Round 10 F1 -- resolve a control UUID to the cycle it is graded in.
 *
 * Global search links a control hit to the assessments hub carrying
 * only the UUID; the hub cannot turn that into a destination on its
 * own. Disabled when there is no control in the URL, which is the
 * ordinary case.
 */
export function useControlLocation(
  controlId: string | null | undefined,
): UseQueryResult<ControlLocation, ApiError> {
  return useQuery({
    queryKey: ["framework-assessments", "control-location", controlId ?? "_none"],
    queryFn: () =>
      api.get<ControlLocation>(
        `/v1/framework/controls/${encodeURIComponent(controlId ?? "")}/location`,
      ),
    enabled: !!controlId,
    // A stale or deleted control id is a 404, and retrying a 404
    // only delays telling the reader the link is dead.
    retry: false,
    staleTime: 60_000,
  });
}

// ── Cycles ─────────────────────────────────────────────────────────

export function useCycles(
  framework: string | undefined,
): UseQueryResult<FrameworkCycle[], ApiError> {
  return useQuery({
    queryKey: frameworkAssessmentKeys.cycles(framework),
    queryFn: () => {
      const query = framework ? `?framework=${encodeURIComponent(framework)}` : "";
      return api.get<FrameworkCycle[]>(`/v1/framework/cycles${query}`);
    },
  });
}

export function useCreateCycle(): UseMutationResult<
  FrameworkCycle,
  ApiError,
  CycleCreateIn
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post<FrameworkCycle>("/v1/framework/cycles", body),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: frameworkAssessmentKeys.all,
      });
    },
  });
}

export function useOpenCycle(): UseMutationResult<
  FrameworkCycle,
  ApiError,
  { cycleId: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ cycleId }) =>
      api.post<FrameworkCycle>(
        `/v1/framework/cycles/${encodeURIComponent(cycleId)}/open`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: frameworkAssessmentKeys.all,
      });
    },
  });
}

export function useCloseCycle(): UseMutationResult<
  FrameworkCycle,
  ApiError,
  { cycleId: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ cycleId }) =>
      api.post<FrameworkCycle>(
        `/v1/framework/cycles/${encodeURIComponent(cycleId)}/close`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: frameworkAssessmentKeys.all,
      });
    },
  });
}

/**
 * P22.1 (#522): admin-only PATCH that toggles
 * `auto_analyse_on_evidence_attach` on a cycle. When True, a
 * preparer's evidence attach kicks off the single-control
 * Marsad Engine analyser automatically.
 */
export function usePatchCycle(): UseMutationResult<
  FrameworkCycle,
  ApiError,
  { cycleId: string; autoAnalyseOnEvidenceAttach?: boolean }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ cycleId, autoAnalyseOnEvidenceAttach }) =>
      api.patch<FrameworkCycle>(`/v1/framework/cycles/${encodeURIComponent(cycleId)}`, {
        auto_analyse_on_evidence_attach: autoAnalyseOnEvidenceAttach,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: frameworkAssessmentKeys.all,
      });
    },
  });
}

// ── Assessments ────────────────────────────────────────────────────

export function useCycleAssessments(
  cycleId: string | undefined,
): UseQueryResult<FrameworkAssessment[], ApiError> {
  return useQuery({
    queryKey: cycleId
      ? frameworkAssessmentKeys.cycleAssessments(cycleId)
      : ["framework-assessments", "assessments", "_disabled"],
    queryFn: () =>
      api.get<FrameworkAssessment[]>(
        `/v1/framework/cycles/${encodeURIComponent(cycleId ?? "")}/assessments`,
      ),
    enabled: !!cycleId,
  });
}

export function useUpsertAssessment(
  cycleId: string,
): UseMutationResult<
  FrameworkAssessment,
  ApiError,
  { controlId: string; body: AssessmentUpsertIn }
> {
  const qc = useQueryClient();
  return useMutation({
    // E1 (round 8) -- this mutation's caller awaits `mutateAsync` and
    // catches, which react-query cannot see. Without this the global
    // failure sink announces the server's detail as well, so one
    // failure reaches the reader twice in two wordings.
    meta: { handled: true },
    mutationFn: ({ controlId, body }) =>
      api.put<FrameworkAssessment>(
        `/v1/framework/cycles/${encodeURIComponent(cycleId)}/assessments/${encodeURIComponent(controlId)}`,
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: frameworkAssessmentKeys.cycleAssessments(cycleId),
      });
    },
  });
}

export function useAssessmentTransition(cycleId: string): UseMutationResult<
  FrameworkAssessment,
  ApiError,
  {
    assessmentId: string;
    action: AssessmentAction;
    body: AssessmentTransitionIn;
  }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ assessmentId, action, body }) => {
      const endpoint = action === "send_back" ? "send-back" : action;
      return api.post<FrameworkAssessment>(
        `/v1/framework/assessments/${encodeURIComponent(assessmentId)}/${endpoint}`,
        body,
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: frameworkAssessmentKeys.cycleAssessments(cycleId),
      });
    },
  });
}

// ── Evidence ───────────────────────────────────────────────────────

export function useEvidence(
  assessmentId: string | undefined,
): UseQueryResult<EvidenceArtefact[], ApiError> {
  return useQuery({
    queryKey: assessmentId
      ? frameworkAssessmentKeys.evidence(assessmentId)
      : ["framework-assessments", "evidence", "_disabled"],
    queryFn: () =>
      api.get<EvidenceArtefact[]>(
        `/v1/framework/assessments/${encodeURIComponent(assessmentId ?? "")}/evidence`,
      ),
    enabled: !!assessmentId,
  });
}

export function useAttachEvidence(
  assessmentId: string,
): UseMutationResult<EvidenceArtefact, ApiError, EvidenceArtefactIn> {
  const qc = useQueryClient();
  return useMutation({
    // E1 (round 8) -- this mutation's caller awaits `mutateAsync` and
    // catches, which react-query cannot see. Without this the global
    // failure sink announces the server's detail as well, so one
    // failure reaches the reader twice.
    meta: { handled: true },
    mutationFn: (body) =>
      api.post<EvidenceArtefact>(
        `/v1/framework/assessments/${encodeURIComponent(assessmentId)}/evidence`,
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: frameworkAssessmentKeys.evidence(assessmentId),
      });
    },
  });
}

export function useDetachEvidence(
  assessmentId: string,
): UseMutationResult<void, ApiError, { linkId: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ linkId }) =>
      api.delete<void>(
        `/v1/framework/assessments/${encodeURIComponent(assessmentId)}/evidence/${encodeURIComponent(linkId)}`,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: frameworkAssessmentKeys.evidence(assessmentId),
      });
    },
  });
}

// ── Analyser ───────────────────────────────────────────────────────

export function useAnalyserRuns(
  cycleId: string | undefined,
): UseQueryResult<AnalyserRun[], ApiError> {
  // P35.2 (#554): poll every 2s while any run for this cycle is
  // queued/running. Stops polling on terminal state. The refetch-
  // interval callback receives the current data via the new
  // function-arg shape introduced in @tanstack/react-query v5.
  return useQuery({
    queryKey: cycleId
      ? frameworkAssessmentKeys.analyserRuns(cycleId)
      : ["framework-assessments", "analyser-runs", "_disabled"],
    queryFn: () =>
      api.get<AnalyserRun[]>(
        `/v1/framework/cycles/${encodeURIComponent(cycleId ?? "")}/analyser-runs`,
      ),
    enabled: !!cycleId,
    refetchInterval: (query) => {
      const data = query.state.data as AnalyserRun[] | undefined;
      if (!data) return false;
      const anyOpen = data.some((r) => r.status === "queued" || r.status === "running");
      return anyOpen ? 2_000 : false;
    },
  });
}

export function useAnalyseControl(
  cycleId: string,
): UseMutationResult<AnalyserRun, ApiError, AnalyseControlIn> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      api.post<AnalyserRun>(
        `/v1/framework/cycles/${encodeURIComponent(cycleId)}/analyse-control`,
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: frameworkAssessmentKeys.analyserRuns(cycleId),
      });
    },
  });
}

/** Round 15 A11 -- a queued run offers cancel; only a run that has not
 * started can be cancelled, and the server says so (409) otherwise. */
export function useCancelAnalyserRun(
  cycleId: string,
): UseMutationResult<AnalyserRun, ApiError, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId) =>
      api.post<AnalyserRun>(
        `/v1/framework/cycles/${encodeURIComponent(cycleId)}/analyser-runs/${encodeURIComponent(runId)}/cancel`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: frameworkAssessmentKeys.analyserRuns(cycleId),
      });
    },
  });
}

export interface RetryAnalyserRunInput {
  runId: string;
  control_id: string;
  evidence_text: string;
  source_artefact_id?: string | null;
}

// P61.2 (#566): retry a failed analyser run. The backend gates on
// backoff via 429 + Retry-After; the AnalyserPanel reads the
// header from the ApiError details to drive its countdown timer.
export function useRetryAnalyserRun(
  cycleId: string,
): UseMutationResult<AnalyserRun, ApiError, RetryAnalyserRunInput> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: ({ runId, ...body }) =>
      api.post<AnalyserRun>(
        `/v1/framework/cycles/${encodeURIComponent(
          cycleId,
        )}/runs/${encodeURIComponent(runId)}/retry`,
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: frameworkAssessmentKeys.analyserRuns(cycleId),
      });
    },
  });
}

export function useAnalyseBatch(
  cycleId: string,
): UseMutationResult<AnalyserRun, ApiError, AnalyseBatchIn> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      api.post<AnalyserRun>(
        `/v1/framework/admin/cycles/${encodeURIComponent(cycleId)}/analyse-batch`,
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: frameworkAssessmentKeys.analyserRuns(cycleId),
      });
      // Batch run materialises drafts in framework_assessments;
      // invalidate cycle-assessments so the grid refreshes.
      void qc.invalidateQueries({
        queryKey: frameworkAssessmentKeys.cycleAssessments(cycleId),
      });
    },
  });
}

// ── B2: what is waiting on a seat ──────────────────────────────────

/**
 * Assessments waiting on one position, across every cycle.
 *
 * The queue view listed CAPs and nothing else, so a preparer who
 * submitted an assessment could not follow it anywhere: My Work found
 * it, the queue she actually looked in did not. Not filtered to the
 * caller, like the CAP queue -- a reviewer may peek at another seat.
 */
/**
 * D9 -- how many assessments each seat holds.
 *
 * The queue chips counted CAPs only, so a reviewer read "1" above a
 * queue listing one CAP and two assessments. A number that disagrees
 * with the list beneath it is worse than no number.
 */
export function useAssessmentQueueCounts(
  // Round 9 E9 -- a seat holding no position cannot read this and has
  // no queue for it to fill. The masthead was asking on every render.
  applies = true,
): UseQueryResult<Record<string, number>, ApiError> {
  return useQuery({
    queryKey: ["framework-assessments", "queue", "counts"],
    queryFn: () =>
      api.get<Record<string, number>>("/v1/framework/assessments/queue/counts"),
    enabled: applies,
  });
}

export function useAssessmentQueue(
  position: string | undefined,
): UseQueryResult<AssessmentQueueRow[], ApiError> {
  return useQuery({
    queryKey: ["framework-assessments", "queue", position ?? "_all"],
    queryFn: () =>
      api.get<AssessmentQueueRow[]>(
        position
          ? `/v1/framework/assessments/queue?position=${encodeURIComponent(position)}`
          : "/v1/framework/assessments/queue",
      ),
    enabled: Boolean(position),
  });
}
